import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@base-app';
import { CardGrid, InfoCard } from '@builder';
import { useModal } from '@components/hooks';
import { COLLECTION_LOCATION, colors, STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { SampleCollectionLocation } from '@modules/study-insights/types';
import { SequencedSamplesProps } from './SequencedSamples.types';
import { BriefcaseIcon, HomeIcon, StyledCardContainer } from './SequencedSamples.styles';

import SamplePercentageBar from '../../shared/SamplePercentageBar/SamplePercentageBar';
import SampleInfoByType from '../../shared/SampleInfoByType/SampleInfoByType';
import SampleTitle from '../../shared/SampleTitle/SampleTitle';
import SampleTotal from '../../shared/SampleTotal/SampleTotal';
import ViewOrderIdsModal from '../../shared/ViewOrderIds/ViewOrderIdsModal/ViewOrderIdsModal';

const SequencedSamples: FC<SequencedSamplesProps> = ({ period, data, stats }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { isShowing, toggle } = useModal();
  const [collectionLocationModal, setCollectionLocationModal] = useState<SampleCollectionLocation>();

  const isSeriesEmptyInPerson = data.IN_PERSON === undefined;
  const isSeriesEmptyAtHome = data.AT_HOME === undefined;
  const isSeriesEmpty = isSeriesEmptyAtHome || isSeriesEmptyInPerson;

  const totalSamples = !isSeriesEmpty ? data.total : 0;
  const atHomeTotal = !isSeriesEmpty ? data.AT_HOME.total : 0;
  const inPersonTotal = !isSeriesEmpty ? data.IN_PERSON.total : 0;
  const percentageAtPerson = stats.percentChanges?.find(
    (percent) => percent.collectionLocation === COLLECTION_LOCATION.PERSON,
  )?.value;
  const percentageAtHome = stats.percentChanges?.find(
    (percent) => percent.collectionLocation === COLLECTION_LOCATION.HOME,
  )?.value;

  const percentageItems = [
    {
      text: t(`sequenced_samples.${SampleCollectionLocation.IN_PERSON.toLocaleLowerCase()}`),
      value: !isSeriesEmpty ? inPersonTotal : 50,
      textColor: colors.darkBlue200,
      bgColor: colors.blue700,
    },
    {
      text: t(`sequenced_samples.${SampleCollectionLocation.AT_HOME.toLocaleLowerCase()}`),
      value: !isSeriesEmpty ? atHomeTotal : 50,
      textColor: colors.darkBlue200,
      bgColor: colors.blue800,
    },
  ];

  const handleToggleModal = (collectionLocation: SampleCollectionLocation): void => {
    setCollectionLocationModal(collectionLocation);
    toggle();
  };

  const handleGraphFormatModal = (): void => {};

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '$6',
      }}
    >
      <SamplePercentageBar
        data-testid="sequencing-data-percentage-bar"
        label={
          !isSeriesEmpty
            ? t('sequenced_samples.total_samples', { totalSamples })
            : t('sequenced_samples.total_samples_unavailable')
        }
        percentageItems={percentageItems}
        isSeriesEmpty={isSeriesEmpty}
      />
      <CardGrid
        title={
          <SampleTitle
            id="sequenced-samples-in-person"
            icon={<BriefcaseIcon />}
            label={t('sequenced_samples.in_person_title')}
            onClickViewOrderIds={() => handleToggleModal(SampleCollectionLocation.IN_PERSON)}
          />
        }
      >
        <StyledCardContainer>
          <InfoCard css={{ flexBasis: '300px' }} separator="right">
            <SampleTotal
              id="sequenced-samples-in-person-total"
              accentColor={'$green200'}
              description={t('sequenced_samples.sequenced_description')}
              onClickButton={handleGraphFormatModal}
              collectionLocation={SampleCollectionLocation.IN_PERSON}
              percentage={percentageAtPerson}
              period={period}
              title={t('sequenced_samples.total_sequenced')}
              totalSamples={data?.IN_PERSON?.totalValid ?? t('data_unavailable')}
            />
          </InfoCard>
          <InfoCard css={{ minWidth: '220px' }} separator="right">
            <SampleTotal
              id="sequenced-samples-in-person-recollect"
              accentColor={'$yellow300'}
              description={t('sequenced_samples.recollects_description')}
              title={t('sequenced_samples.total_recollects')}
              totalSamples={data?.IN_PERSON?.totalRecollect ?? t('data_unavailable')}
            />
          </InfoCard>
          <InfoCard>
            <SampleInfoByType
              id="in-person"
              data={data?.IN_PERSON as any}
              isSeriesEmpty={isSeriesEmptyInPerson}
              title={t('sample_type')}
            />
          </InfoCard>
        </StyledCardContainer>
      </CardGrid>
      <CardGrid
        title={
          <SampleTitle
            id="sequenced-samples-at-home"
            icon={<HomeIcon />}
            label={t('sequenced_samples.at_home_title')}
            onClickViewOrderIds={() => handleToggleModal(SampleCollectionLocation.AT_HOME)}
          />
        }
      >
        <StyledCardContainer>
          <InfoCard css={{ flexBasis: '300px' }} separator="right">
            <SampleTotal
              id="sequenced-samples-at-home-total"
              accentColor={'$green200'}
              description={t('sequenced_samples.sequenced_description')}
              onClickButton={handleGraphFormatModal}
              collectionLocation={SampleCollectionLocation.AT_HOME}
              percentage={percentageAtHome}
              period={period}
              title={t('sequenced_samples.total_sequenced')}
              totalSamples={data?.AT_HOME?.totalValid ?? t('data_unavailable')}
            />
          </InfoCard>
          <InfoCard css={{ minWidth: '220px' }} separator="right">
            <SampleTotal
              id="sequenced-samples-at-home-recollect"
              accentColor={'$yellow300'}
              description={t('sequenced_samples.recollects_description')}
              title={t('sequenced_samples.total_recollects')}
              totalSamples={data?.AT_HOME?.totalRecollect ?? t('data_unavailable')}
            />
          </InfoCard>
          <InfoCard>
            <SampleInfoByType
              id="at-home"
              collectionLocation={SampleCollectionLocation.AT_HOME}
              data={data.AT_HOME as any}
              isSeriesEmpty={isSeriesEmptyAtHome}
              title={t('sample_type')}
            />
          </InfoCard>
        </StyledCardContainer>
      </CardGrid>
      {collectionLocationModal && (
        <ViewOrderIdsModal
          isOpen={isShowing}
          title={t(`view_order_id.title.sequenced_samples.${collectionLocationModal.toLocaleLowerCase()}`)}
          onClose={() => toggle()}
          collectionLocation={collectionLocationModal}
        />
      )}
    </Box>
  );
};

export default SequencedSamples;
